// Default country used only as a placeholder until the user's real selection
// (persisted in useRegionStore) is known. Keep this the single source for the
// default — do not reintroduce per-file "it"/"us"/"cu" fallbacks.
export const DEFAULT_COUNTRY_CODE = "cu";

// Payment provider helpers

export const STRIPE_PROVIDER_ID = "pp_stripe_stripe";
export const PAYPAL_PROVIDER_ID = "pp_paypal-payment-metadata_paypal-payment-metadata";
export const TROPIPAY_PROVIDER_ID = "pp_tropipay_tropipay";
export const MANUAL_PROVIDER_ID = "pp_system_default";

export type PaymentProvider = {
  /** Current/canonical provider id (used when creating payment sessions). */
  id: string;
  /**
   * Substrings that identify this provider in a payment `provider_id`. A
   * provider_id belongs here if it contains ANY of these. Keeping a LIST (not a
   * single id) means historical ids keep resolving after a provider is renamed:
   * add the old fragment alongside the new one and existing orders still map to
   * the right label.
   */
  match: string[];
  name: string;
  description: string;
};

// Single source of truth for how a payment provider is presented anywhere in
// the storefront. Add a new provider (or a renamed id) HERE — never re-map a
// provider_id to a label inline in a component.
export const PAYMENT_PROVIDERS: PaymentProvider[] = [
  {
    id: STRIPE_PROVIDER_ID,
    match: ["stripe"],
    name: "Tarjeta de Crédito/Débito",
    description: "Paga de forma segura con Stripe",
  },
  {
    id: PAYPAL_PROVIDER_ID,
    match: ["paypal"],
    name: "PayPal",
    description: "Paga de forma segura con tu cuenta PayPal",
  },
  {
    id: TROPIPAY_PROVIDER_ID,
    match: ["tropipay"],
    name: "Tropipay",
    description: "Paga con tarjeta a través de Tropipay",
  },
  {
    id: "pp_laberinto_laberinto",
    match: ["laberinto"],
    name: "Laberinto",
    description: "Paga con Laberinto (Cuba)",
  },
  {
    id: MANUAL_PROVIDER_ID,
    // If MANUAL_PROVIDER_ID is ever renamed (e.g. to "pp_platform_default"),
    // add "platform_default" here so orders placed under the old id still map.
    match: ["system_default", "manual"],
    name: "Pago por defecto",
    description: "Pago manual",
  },
];

const DEFAULT_PAYMENT_LABEL = {
  name: "Tarjeta de crédito",
  description: "",
} as const;

/** Resolve the provider definition for a payment `provider_id` (old or new). */
export const getPaymentProvider = (
  providerId?: string
): PaymentProvider | undefined => {
  if (!providerId) return undefined;
  const id = providerId.toLowerCase();
  return PAYMENT_PROVIDERS.find((p) =>
    p.match.some((fragment) => id.includes(fragment))
  );
};

/**
 * Human label + description for a payment `provider_id`, with a safe fallback.
 * Use this everywhere a payment method is shown to the customer.
 */
export const getPaymentLabel = (
  providerId?: string
): { name: string; description: string } => {
  const provider = getPaymentProvider(providerId);
  return provider
    ? { name: provider.name, description: provider.description }
    : DEFAULT_PAYMENT_LABEL;
};

// Provider-type predicates — used to render provider-specific checkout UI
// (Stripe card form vs PayPal buttons vs Tropipay redirect). Distinct concern
// from the label lookup above.
export const isStripe = (providerId?: string): boolean =>
  providerId?.startsWith("pp_stripe") ?? false;

export const isPaypal = (providerId?: string): boolean =>
  providerId?.startsWith("pp_paypal") ?? false;

export const isTropipay = (providerId?: string): boolean =>
  providerId?.startsWith("pp_tropipay") ?? false;

export const isManual = (providerId?: string): boolean =>
  getPaymentProvider(providerId)?.id === MANUAL_PROVIDER_ID;

/**
 * Discriminator on placeOrder's non-order result. It lives here, not next to
 * placeOrder, because that file is "use server" and such files may only export
 * async functions — exporting a const from one breaks the whole module at build
 * time (invisible to tsc, so only `next build` catches it).
 */
export const LOCATION_DISABLED = "LOCATION_DISABLED";
